/**
 * Remote scope facets and legacy path parsing (`/jobs/remote/…`, `/tyopaikat/etätyö/…`).
 * Canonical indexed URLs use the same location-hub shape as cities: `/en/jobs/location/remote_finland`, etc.
 */

export type RemoteHubEnglishScope = 'Finland' | 'Europe';

const SCOPE_ALIASES: Record<string, RemoteHubEnglishScope> = {
  finland: 'Finland',
  suomi: 'Finland',
  europe: 'Europe',
  eurooppa: 'Europe',
  europa: 'Europe',
};

/** Decode a URL path segment (e.g. Suomi, Eurooppa) to canonical English scope for routing. */
export function remoteScopeSlugToEnglishScope(raw: string): RemoteHubEnglishScope | null {
  const key = raw.trim().toLowerCase();
  return SCOPE_ALIASES[key] ?? null;
}

/** Algolia location facet for remote hub pages (matches enriched job location_* fields). */
export function remoteHubScopeToLocationFacet(
  locale: 'en' | 'fi' | 'sv',
  scope: RemoteHubEnglishScope
): string {
  if (scope === 'Finland') {
    if (locale === 'fi') return 'Etätyö - Suomi';
    if (locale === 'sv') return 'Distans - Finland';
    return 'Remote - Finland';
  }
  if (locale === 'fi') return 'Etätyö - Eurooppa';
  if (locale === 'sv') return 'Distans - Europa';
  return 'Remote - Europe';
}

/** Logical English path segment after `/jobs/remote/` (Finland | Europe). */
export function englishScopeSlugForRemoteHub(scope: RemoteHubEnglishScope): string {
  return scope === 'Finland' ? 'Finland' : 'Europe';
}

/** Localized path segment after `/…/remote/`, `/…/etätyö/`, `/…/distans/`. */
export function localizedRemoteScopeSlug(locale: 'en' | 'fi' | 'sv', scope: RemoteHubEnglishScope): string {
  if (scope === 'Finland') {
    return locale === 'fi' ? 'Suomi' : 'Finland';
  }
  if (locale === 'fi') return 'Eurooppa';
  if (locale === 'sv') return 'Europa';
  return 'Europe';
}

/** Path segment after `/jobs/` / `/tyopaikat/` / `/lediga-jobb/` for remote hubs. */
export function isRemotePathMarker(segment: string): boolean {
  let s = segment;
  try {
    s = decodeURIComponent(segment);
  } catch {
    // ignore
  }
  const n = s.toLowerCase();
  return n === 'remote' || n === 'etätyö' || n === 'etatyö' || n === 'distans';
}

/**
 * Matches any "Remote - X" style location facet/label ("Remote - Finland", "Etätyö - Suomi",
 * "Distans - Europa", …) regardless of scope, locale, or whether it's a scope we've hardcoded.
 * Used to route every remote hub page to the single shared background image rather than the
 * per-location lookup: see known-location-backgrounds.ts.
 */
export function isRemoteScopeLocationLabel(location: string): boolean {
  if (!location || typeof location !== 'string') return false;
  return /^(remote|etätyö|etatyö|distans)\s*-\s*.+/i.test(location.trim());
}
